import { Controller } from '@hotwired/stimulus'
import { csrfToken } from '../utils/csrf'

// Éditeur visuel de l'admin (doc/texte-riche.md) : Tiptap, chargé à part (rich_editor.js, `src` et `css`)
// pour ne peser que sur ces formulaires. Le HTML de l'éditeur est recopié dans le <textarea> caché
// à chaque modification ; le serveur le nettoie au rendu (RichText::Sanitizer).
const COMMANDS = {
  bold: [(chain) => chain.toggleBold(), 'bold'],
  italic: [(chain) => chain.toggleItalic(), 'italic'],
  heading2: [(chain) => chain.toggleHeading({ level: 2 }), 'heading', { level: 2 }],
  heading3: [(chain) => chain.toggleHeading({ level: 3 }), 'heading', { level: 3 }],
  bulletList: [(chain) => chain.toggleBulletList(), 'bulletList'],
  orderedList: [(chain) => chain.toggleOrderedList(), 'orderedList'],
  blockquote: [(chain) => chain.toggleBlockquote(), 'blockquote'],
  undo: [(chain) => chain.undo()],
  redo: [(chain) => chain.redo()],
}

export default class extends Controller {
  static targets = ['editor', 'input', 'button', 'file', 'notice', 'prompt', 'promptInput', 'promptSponsored', 'promptSponsoredInput', 'sentinel', 'sticky']
  static values = { src: String, css: String, uploadUrl: String, record: String, labels: Object }

  async connect() {
    this.#observeSticking()
    this.#loadStylesheet()
    const { createRichEditor } = await import(this.srcValue)
    // Page quittée pendant le chargement.
    if (!this.element.isConnected || this.editor) return

    // Une page restaurée par Turbo contient encore l'éditeur de la visite précédente, figé.
    this.editorTarget.replaceChildren()
    this.editor = createRichEditor({
      element: this.editorTarget,
      content: this.inputTarget.value,
      placeholder: this.inputTarget.placeholder,
      attributes: { class: this.editorTarget.dataset.editorClass },
      onUpdate: () => this.#sync(),
      onFiles: (files) => this.#upload(files),
    })
    this.editor.on('transaction', () => this.#refreshButtons())
    this.#refreshButtons()
    this.form?.addEventListener('submit', this.sync)
  }

  disconnect() {
    this.stickingObserver?.disconnect()
    this.form?.removeEventListener('submit', this.sync)
    this.editor?.destroy()
    this.editor = null
  }

  // Les boutons existent avant que Tiptap ait fini de charger : sans éditeur, ils ne font rien.
  run({ params: { command } }) {
    if (!this.editor) return

    const [apply] = COMMANDS[command]
    apply(this.editor.chain().focus()).run()
  }

  link() {
    if (!this.editor) return

    const { href: previous = '', rel = '' } = this.editor.getAttributes('link')
    this.promptSponsoredInputTarget.checked = rel.split(' ').includes('sponsored')
    this.#ask(this.labelsValue.link_prompt, previous, (url) => {
      const chain = this.editor.chain().focus().extendMarkRange('link')
      if (!url) return chain.unsetLink().run()

      const attrs = { href: url, rel: this.promptSponsoredInputTarget.checked ? 'noopener sponsored' : 'noopener' }
      // Sans texte choisi, l'adresse elle-même devient le lien.
      if (this.editor.state.selection.empty && !previous) {
        return chain.insertContent({ type: 'text', text: url, marks: [{ type: 'link', attrs }] }).run()
      }
      chain.setLink(attrs).run()
    }, { sponsored: true })
  }

  image() {
    if (!this.editor) return

    this.fileTarget.click()
  }

  // Le texte alternatif de l'image sélectionnée (lu par les lecteurs d'écran et les moteurs de
  // recherche) ; sans description, le serveur met le titre du contenu.
  imageAlt() {
    if (!this.editor) return
    if (!this.editor.isActive('image')) return this.#notify(this.labelsValue.image_alt_missing)

    const previous = this.editor.getAttributes('image').alt ?? ''
    this.#ask(this.labelsValue.image_alt_prompt, previous, (alt) => {
      this.editor.chain().focus().updateAttributes('image', { alt: alt || null }).run()
    })
  }

  fileChosen() {
    this.#upload(Array.from(this.fileTarget.files))
    this.fileTarget.value = ''
  }

  youtube() {
    if (!this.editor) return

    this.#ask(this.labelsValue.youtube_prompt, '', (url) => {
      if (!url) return

      const inserted = this.editor.chain().focus().setYoutubeVideo({ src: url }).run()
      if (!inserted) this.#notify(this.labelsValue.youtube_invalid)
    })
  }

  confirmPrompt() {
    const answer = this.onAnswer
    const value = this.promptInputTarget.value.trim()
    this.closePrompt()
    answer?.(value)
  }

  closePrompt() {
    this.onAnswer = null
    this.promptTarget.classList.add('hidden')
  }

  sync = () => this.#sync()

  get form() {
    return this.element.closest('form')
  }

  #sync() {
    this.inputTarget.value = this.editor.isEmpty ? '' : this.editor.getHTML()
  }

  #refreshButtons() {
    this.buttonTargets.forEach((button) => {
      const [, name, attributes] = COMMANDS[button.dataset.richEditorCommandParam] ?? []
      if (name) button.setAttribute('aria-pressed', this.editor.isActive(name, attributes))
    })
  }

  // Gods::RichTextImagesController répond l'adresse de l'image, insérée à la position du curseur.
  async #upload(files) {
    for (const file of files) {
      this.#notify(this.labelsValue.uploading, false)
      try {
        const { url, width, height } = await this.#send(file)
        this.editor.chain().focus().setImage({ src: url, width, height }).run()
        this.#notify('')
      } catch (error) {
        this.#notify(error.message)
      }
    }
  }

  async #send(file) {
    const body = new FormData()
    body.append('image', file)
    if (this.recordValue) body.append('record', this.recordValue)

    const response = await fetch(this.uploadUrlValue, {
      method: 'POST',
      body,
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
    })
    const json = await response.json().catch(() => ({}))
    if (!response.ok || !json.url) throw new Error(json.error || this.labelsValue.upload_failed)

    return json
  }

  // La barre d'adresse sous la barre d'outils ; l'éditeur garde sa sélection pendant la saisie,
  // `focus()` la rend avant d'appliquer la réponse.
  #ask(label, value, onAnswer, { sponsored = false } = {}) {
    this.onAnswer = onAnswer
    this.promptSponsoredTarget.classList.toggle('hidden', !sponsored)
    Object.assign(this.promptInputTarget, { value, placeholder: label })
    this.promptInputTarget.setAttribute('aria-label', label)
    this.promptTarget.classList.remove('hidden')
    this.promptInputTarget.focus()
    this.promptInputTarget.select()
  }

  // Un message près de la barre d'outils, en rouge pour une erreur ; vide pour l'effacer.
  #notify(message, error = true) {
    this.noticeTarget.textContent = message
    this.noticeTarget.classList.toggle('hidden', !message)
    this.noticeTarget.classList.toggle('text-red-600', error)
    this.noticeTarget.classList.toggle('text-gray-500', !error)
  }

  // La barre d'outils est collée quand le repère posé juste au-dessus passe sous son seuil
  // (`top` du bloc collant) : `data-stuck` lui retire ses coins arrondis.
  #observeSticking() {
    this.stickingObserver?.disconnect()
    const top = parseFloat(getComputedStyle(this.stickyTarget).top) || 0
    this.stickingObserver = new IntersectionObserver(
      ([entry]) => {
        const stuck = !entry.isIntersecting && entry.boundingClientRect.top < top
        this.stickyTarget.toggleAttribute('data-stuck', stuck)
      },
      { rootMargin: `-${top}px 0px 0px 0px` },
    )
    this.stickingObserver.observe(this.sentinelTarget)
  }

  #loadStylesheet() {
    if (document.head.querySelector(`link[href="${this.cssValue}"]`)) return

    const link = document.createElement('link')
    Object.assign(link, { rel: 'stylesheet', href: this.cssValue })
    document.head.append(link)
  }
}
